import { DeleteOutlined } from '@ant-design/icons';
import { IWidgetParsedProps } from '@framework/engine';
import {
  ComputedStaticTextInput,
  FormItem,
  SelectInput,
  StaticInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { ContentHoverEditButtonProps } from '@framework/ui';
import { Button, Col } from 'antd';
import clx from 'classnames';
import styled from 'styled-components';
import {
  IMultiPreferenceRow,
  multiPreferenceFieldKey,
} from '../createMultiPreferenceFormSchema';
import { IMultiPreferenceWidget } from '../IMultiPreferenceWidget';
import { useMultiPreferenceSelect } from './hooks/useMultiPreferenceSelect';
import { MultiPreferenceRowWrapper } from './MultiPreferenceRowWrapper';

export interface IMultiPreferenceRowProps
  extends Omit<ContentHoverEditButtonProps, 'children'> {
  /**
   * Nome del campo array (non del singolo elemento) a cui è associato il widget
   */
  name: string;
  index: number;
  widget: IMultiPreferenceWidget;
  options: IWidgetParsedProps;
  draggingItem?: IMultiPreferenceRow;
}

const MultiPreferenceRowDiv = styled.div`
  &.is-space-for-drag {
    opacity: 0;
  }
`;

export function MultiPreferenceRow(props: Readonly<IMultiPreferenceRowProps>) {
  const {
    index,
    widget,
    options,
    name,
    isSpaceForDrag,
    isDragging,
    draggingItem,
    ...rest
  } = props;

  const { fields, computedIdValue, handleResetOnChange } =
    useMultiPreferenceSelect(widget.cascade, index, name);

  const { t } = useTranslation();

  return (
    <MultiPreferenceRowDiv
      className={clx({
        'is-space-for-drag': isSpaceForDrag,
      })}
    >
      <MultiPreferenceRowWrapper
        index={index}
        readonly={options.readonly}
        action={
          !options.readonly && (
            <Button
              type="default"
              danger
              size="large"
              icon={<DeleteOutlined aria-hidden />}
              onClick={e => {
                e.stopPropagation();
                rest.onRemove?.(e);
              }}
            >
              {t('multi-preference-widget.remove-row', {
                defaultValue: 'Rimuovi',
              })}
            </Button>
          )
        }
      >
        {fields?.map((field, i) => {
          const fieldKey = multiPreferenceFieldKey(field.field.index);
          const fieldName = `${options.name}[${index}].${fieldKey}`;

          if (isDragging) {
            return (
              <Col key={field.field.index}>
                <StaticInput value={draggingItem?.[fieldKey]} />
              </Col>
            );
          }

          return (
            <Col key={field.field.index}>
              <FormItem name={fieldName}>
                <SelectInput
                  name={fieldName}
                  // Non possiamo usare il reset normale perché non funzionerebbe
                  // nella situazione del riordinamento (D&D).
                  // Per questo motivo usiamo `handleResetOnChange` che resetta i
                  // campi successivi all'onChange del campo corrente
                  skipResetOnMissingValue
                  // Liste con opzioni lunghe
                  rootClassName="full-text-options multi-preference-options"
                  disabled={field.disabled ? true : undefined}
                  readOnly={options.readonly}
                  placeholder={field.field.label ?? `Preferenza ${i + 1}`} // prettier-ignore
                  onChange={(value, option, onChange) => {
                    onChange(value);
                    handleResetOnChange(value, i);

                    // Scommentare in caso si voglia gestire l'aggiunta automatica di una nuova riga
                    // if (i === fields.length - 1) {
                    //   handleAdd();
                    // }
                  }}
                  options={field.options}
                />
              </FormItem>
            </Col>
          );
        })}

        {!isDragging && (
          <ComputedStaticTextInput
            name={`${options.name}[${index}].id`}
            value={computedIdValue}
            readonly={options.readonly}
            hidden
          />
        )}
      </MultiPreferenceRowWrapper>
    </MultiPreferenceRowDiv>
  );
}
